import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { enqueueRpc } from "../../../lib/enqueueRpc";
import { hasQuantity } from "../../../lib/quantityInput";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { useStockSource } from "../../../hooks/useStockSource";
import { Button } from "../../../components/ui/button";
import { useScanPrefill } from "../../../hooks/useScanPrefill";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { QuantityStepper } from "../../../components/mobile/QuantityStepper";
import { PositionPicker } from "../../../components/mobile/PositionPicker";
import { ArticlePhotoPreview } from "../../../components/mobile/ArticlePhotoPreview";
import { serialsByPositionFrom } from "../../../lib/serialsByPosition";
import { ScanBox } from "../../../components/mobile/ScanBox";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

export default function EntnahmeFlow() {
    const flow = useFlowOperationId();
    const prefill = useScanPrefill();
    const [positions, setPositions] = useState([]);
    const [articles, setArticles] = useState({});
    const [compartments, setCompartments] = useState({});
    const [serialsByPosition, setSerialsByPosition] = useState({});
    const [positionId, setPositionId] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [reason, setReason] = useState("project_need");
    const [reference, setReference] = useState("");
    const [person, setPerson] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [scanRestartKey, setScanRestartKey] = useState(0);

    useEffect(() => {
        Promise.all([
            rpc.get("/master/stock-positions"),
            rpc.get("/master/articles"),
            rpc.get("/master/compartments"),
            rpc.get("/master/assets?limit=5000"),
        ]).then(([p, a, c, s]) => {
            if (p.ok) setPositions(p.data);
            if (a.ok) setArticles(Object.fromEntries(a.data.map((x) => [x.id, x])));
            if (c.ok) setCompartments(Object.fromEntries(c.data.map((x) => [x.id, x])));
            // #182: Seriennummern der Geräte je Position für die Suche
            if (s.ok) setSerialsByPosition(serialsByPositionFrom(s.data));
        });
    }, []);

    const selectedArticle = articles[positions.find((p) => p.id === positionId)?.article_id];

    const { sourcePositions, scanSource, resetSource, sourceLabel } = useStockSource(positions, compartments, prefill, setPositionId);

    const submit = async () => {
        if (!positionId) return toast.error("Bitte eine Bestands-Position wählen.");
        if (!hasQuantity(quantity)) return toast.error("Menge eingeben.");
        setSubmitting(true);
        const artName = articles[positions.find((p) => p.id === positionId)?.article_id]?.name;
        const res = await enqueueRpc({
            method: "post",
            path: "/movements",
            rpcName: "Entnahme",
            summary: `${quantity} Stk${artName ? " · " + artName : ""}`,
            payload: {
                client_operation_id: flow.clientOperationId,
                type: "out",
                from_position_id: positionId,
                quantity,
                reason,
                reference_value: reference || undefined,
                person: person || undefined,
            },
        });
        setSubmitting(false);
        if (!res.ok) {
            if (res.retryable) {
                toast.warning("Offline -- Entnahme wartet in der Queue.");
            } else {
                toast.error(res.error);
                return;
            }
        } else {
            toast.success(`Entnahme gebucht (Menge ${quantity}).`);
        }
        // Reset for "Weiter scannen"
        setPositions(current => current.map(position => position.id === positionId
            ? { ...position, quantity: Math.max(0, position.quantity - quantity) } : position));
        if (res.ok) {
            const refreshed = await rpc.get("/master/stock-positions");
            if (refreshed.ok) setPositions(refreshed.data);
        }
        flow.rotate();
        setPositionId("");
        setQuantity(1);
        setScanRestartKey((key) => key + 1);
    };

    return (
        <FlowShell
            testId="flow-entnahme"
            title="Entnahme"
            kind="Schnellflow"
            subtitle="Scan → Menge → Buchen"
            onSubmit={submit}
            submitDisabled={!positionId || !hasQuantity(quantity)}
            submitting={submitting}
        >
            <ScanBox
                placeholder="Position / Fach scannen"
                restartKey={scanRestartKey}
                onResolved={(raw, resolved) => {
                    if (["compartment", "article"].includes(resolved?.kind)) {
                        scanSource(resolved);
                    } else {
                        toast.error(`Code nicht zugeordnet: ${raw}`);
                    }
                }}
            />
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Bestands-Position
                </Label>
                {sourceLabel ? <div className="text-sm">
                    <p role="status">{sourceLabel}</p>
                    <Button type="button" variant="outline" className="min-h-11" onClick={resetSource}>Andere Quelle wählen</Button>
                </div> : null}
                {sourcePositions.length === 0 ? <p role="status">Kein Bestand in dieser Quelle.</p> : null}
                <PositionPicker
                    value={positionId}
                    onChange={setPositionId}
                    articles={articles}
                    positions={sourcePositions}
                    compartments={compartments}
                    serialsByPosition={serialsByPosition}
                    testId="entnahme-position-select"
                />
                <ArticlePhotoPreview
                    articleId={selectedArticle?.id}
                    articleName={selectedArticle?.name}
                    testId="entnahme-article-photo"
                />
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Menge
                </Label>
                <QuantityStepper value={quantity} onChange={setQuantity} min={1} testId="entnahme-qty" />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Grund</Label>
                    <Select value={reason} onValueChange={setReason}>
                        <SelectTrigger className="h-12" aria-label="Grund" data-testid="entnahme-reason">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="project_need">Projekt</SelectItem>
                            <SelectItem value="restock">Nachfüllen</SelectItem>
                            <SelectItem value="relocation">Umlagerung</SelectItem>
                            <SelectItem value="quality">Qualität</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-col gap-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Projekt / Ticket
                    </Label>
                    <Input
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        className="h-12"
                        aria-label="Projekt / Ticket"
                    data-testid="entnahme-reference"
                    />
                </div>
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Für Person
                </Label>
                <Input
                    value={person}
                    onChange={(e) => setPerson(e.target.value)}
                    placeholder="Name / Team (optional)"
                    className="h-12"
                    aria-label="Für Person"
                    data-testid="entnahme-person"
                />
            </div>
        </FlowShell>
    );
}
